/**
 * Canonical ordered status keys per facturation document type.
 * Mirrors DocumentCreation.js getStatusOptions — Archives (`reorderStatusesLikeDocumentCreation`) uses this order.
 *
 * VENTES (vérifié vs `ventesDocumentPickerMeta.js` + Nouveau Document) :
 * — proforma, invoice : pending → draft → paid → partially_paid → overdue → cancelled
 * — material-exit (+ delivery-*): pending → draft → partial → delivered → in_transit → canceled
 * — payment-receipt : pending → completed → verified
 * — client-return-slip : pending → received → processed → refunded
 * — credit-note : pending → issued → applied
 * (+ quote dans la même config)
 */

/** @type {Record<string, string[]>} */
const ORDER_UNDERSCORE = {
  quote: ['pending', 'draft', 'accepted', 'rejected', 'negotiating', 'expired'],
  proforma: ['pending', 'draft', 'paid', 'partially_paid', 'overdue', 'cancelled'],
  invoice: ['pending', 'draft', 'paid', 'partially_paid', 'overdue', 'cancelled'],
  quote_request: ['pending', 'draft', 'rejected', 'expired', 'sent', 'answered'],
  proforma_request: ['pending', 'draft', 'rejected', 'expired', 'sent', 'answered'],
  purchase_order: ['pending', 'draft', 'paid', 'partially_paid', 'overdue', 'cancelled'],
  delivery: ['pending', 'draft', 'partial', 'delivered', 'in_transit', 'canceled'],
  material_exit: ['pending', 'draft', 'partial', 'delivered', 'in_transit', 'canceled'],
  shipping: ['pending', 'shipped', 'delivered'],
  shipping_slip: ['pending', 'shipped', 'delivered'],
  return_slip: ['pending', 'processing', 'received', 'accepted', 'rejected', 'processed', 'refunded', 'canceled'],
  receipt_note: ['pending', 'partial', 'received'],
  reception_note: ['pending', 'partial', 'received'],
  payment_slip: ['pending', 'paid', 'processing'],
  payment_voucher: ['pending', 'paid', 'processing'],
  payment_receipt: ['pending', 'completed', 'verified'],
  credit_note: ['pending', 'issued', 'applied'],
  client_return_slip: ['pending', 'received', 'processed', 'refunded'],
  exit_slip: ['pending', 'canceled', 'processed'],
  transfer_slip: ['pending', 'canceled', 'transferred'],
  stock_entry: ['pending', 'received', 'verified'],
  exit_note: ['pending', 'canceled', 'processed'],
  transfer_note: ['pending', 'canceled', 'transferred'],
};

ORDER_UNDERSCORE.default = ['pending', 'draft', 'sent', 'approved', 'rejected', 'cancelled'];

function toUnderscore(typeKey) {
  if (typeKey == null || typeKey === '') return '';
  return String(typeKey).replace(/-/g, '_');
}

/** Normalize DB/UI variants so delivery_note and delivery share one canonical order row. */
const ORDER_LOOKUP_ALIASES = {
  delivery_note: 'delivery',
  reception_note: 'receipt_note',
  exit_note: 'exit_slip',
  transfer_note: 'transfer_slip',
};

export function getCanonicalStatusOrder(documentTypeKey) {
  let u = toUnderscore(documentTypeKey);
  if (ORDER_UNDERSCORE[u]) return ORDER_UNDERSCORE[u];
  u = ORDER_LOOKUP_ALIASES[u] || u;
  if (ORDER_UNDERSCORE[u]) return ORDER_UNDERSCORE[u];
  return ORDER_UNDERSCORE.default;
}

/**
 * Reorders allowed statuses to match Nouveau Document canonical order.
 * Extra statuses not in canonical list append at end (stable relative order preserved).
 */
export function reorderStatusesLikeDocumentCreation(documentTypeKey, statuses) {
  if (!Array.isArray(statuses) || statuses.length <= 1) return statuses || [];
  const order = getCanonicalStatusOrder(documentTypeKey);
  const rank = new Map(order.map((s, i) => [s, i]));
  const known = statuses.filter((s) => rank.has(s));
  const extrasInOrder = statuses.filter((s) => !rank.has(s));

  const sortedKnown = known.sort((a, b) => rank.get(a) - rank.get(b));
  const seen = new Set(sortedKnown);
  const appended = extrasInOrder.filter((s) => {
    if (seen.has(s)) return false;
    seen.add(s);
    return true;
  });

  return [...sortedKnown, ...appended];
}

function normalizeStatusKey(status) {
  if (status == null) return '';
  return String(status).trim().toLowerCase();
}

function foldAccents(value) {
  return normalizeStatusKey(value).normalize('NFD').replace(/[\u0300-\u036f]+/g, '');
}

/**
 * Map canonical DB status → UI key used in Archives menus and DocumentCreation dropdowns.
 * Mirror inverse of backend 'normalizeArchiveStatusForDb' (archives.js).
 */
export function mapArchiveDbStatusToUiStatus(archiveTypeKey, dbStatus) {
  if (dbStatus == null || dbStatus === '') return dbStatus;
  const st = normalizeStatusKey(dbStatus);
  const raw = String(archiveTypeKey || '').trim();
  const kn = raw.replace(/-/g, '_');

  if (kn === 'credit_note') {
    if (st === 'sent') return 'issued';
    if (st === 'completed') return 'applied';
    return st;
  }

  if ((kn === 'quote_request' || kn === 'proforma_request') && st === 'completed') {
    return 'answered';
  }

  if (kn === 'quote' && st === 'processing') return 'negotiating';
  if (kn === 'quote' && st === 'approved') return 'accepted';

  if (
    (kn === 'quote' || kn === 'quote_request' || kn === 'proforma_request') &&
    st === 'overdue'
  ) {
    return 'expired';
  }

  if (['invoice', 'proforma', 'purchase_order'].includes(kn) && st === 'partial') {
    return 'partially_paid';
  }

  if (
    (kn === 'delivery' || kn === 'delivery_note' || kn === 'material_exit') &&
    st === 'shipped'
  ) {
    return 'in_transit';
  }

  if (kn === 'transfer_slip' && st === 'completed') return 'transferred';

  if (kn === 'return_slip' || kn === 'client_return_slip') {
    if (st === 'delivered') return 'received';
    if (st === 'completed') return 'processed';
    if (st === 'paid') return 'refunded';
    if (st === 'approved') return 'accepted';
    return st;
  }

  if (kn === 'exit_slip' && st === 'completed') return 'processed';

  // Bordereau d'expédition : « Livré » = delivered (not « Reçu » / received — that is for réceptions)
  if (
    (kn === 'shipping' || kn === 'shipping_slip' || raw === 'shipping-slip') &&
    st === 'received'
  ) {
    return 'delivered';
  }

  if (kn === 'receipt_note' || kn === 'reception_note') {
    if (st === 'delivered') return 'received';
  }

  if (kn === 'payment_receipt' && st === 'completed') return 'completed';

  return st;
}

/** UI / French labels → SQLite CHECK value (subset of backend archives.js synonyms). */
const LEGACY_STATUS_TO_DB = {
  received: 'delivered',
  receptionne: 'delivered',
  processed: 'completed',
  traite: 'completed',
  refunded: 'paid',
  rembourse: 'paid',
  remboursee: 'paid',
  accepted: 'approved',
  canceled: 'cancelled',
  issued: 'sent',
  applied: 'completed',
  answered: 'completed',
  consolidated: 'completed',
  expired: 'overdue',
  expire: 'overdue',
  negotiating: 'processing',
  partially_paid: 'partial',
  partiallypaid: 'partial',
  transferred: 'completed',
  in_transit: 'shipped',
  verified: 'completed',
  emise: 'sent',
  emis: 'sent',
  envoyee: 'sent',
  envoye: 'sent',
  termine: 'completed',
  terminee: 'completed',
  expedie: 'shipped',
  expediee: 'shipped',
  livre: 'delivered',
  livree: 'delivered',
  attente: 'pending',
  'en attente': 'pending',
  brouillon: 'draft',
  annule: 'cancelled',
  annulee: 'cancelled',
  paye: 'paid',
  payee: 'paid',
  regle: 'paid',
  reglee: 'paid',
  encaisse: 'paid',
  encaissee: 'paid',
  partiel: 'partial',
  partielle: 'partial',
  echu: 'overdue',
  echue: 'overdue',
  'en retard': 'overdue',
  valide: 'approved',
  validee: 'approved',
  approuve: 'approved',
  approuvee: 'approved',
  refuse: 'rejected',
  refusee: 'rejected',
  rejete: 'rejected',
  rejetee: 'rejected',
  'en cours': 'processing',
  encours: 'processing',
};

function legacyLabelToDbStatus(raw) {
  const lower = normalizeStatusKey(raw);
  if (!lower) return '';
  if (LEGACY_STATUS_TO_DB[lower]) return LEGACY_STATUS_TO_DB[lower];
  const fold = foldAccents(raw);
  if (LEGACY_STATUS_TO_DB[fold]) return LEGACY_STATUS_TO_DB[fold];
  return lower;
}

function pickAllowedWithSynonyms(allowed, key) {
  if (!key) return null;
  if (allowed.includes(key)) return key;
  if (key === 'cancelled' && allowed.includes('canceled')) return 'canceled';
  if (key === 'canceled' && allowed.includes('cancelled')) return 'cancelled';
  return null;
}

/**
 * Resolve a form dropdown status from archive DB value and/or legacy fields.
 * @param {string} typeKey - document type id (hyphen or underscore)
 * @param {string} rawStatus - primary status (usually document.status from DB)
 * @param {string[]} [extraCandidates] - legacy mirrors (creditNoteStatus, returnStatus, …)
 */
export function resolveFormStatusFromArchive(typeKey, rawStatus, extraCandidates = []) {
  const allowed = getCanonicalStatusOrder(typeKey);
  const candidates = [rawStatus, ...(extraCandidates || [])].filter(
    (v) => v != null && String(v).trim() !== '',
  );

  if (candidates.length === 0) {
    return allowed.includes('pending') ? 'pending' : allowed[0] || 'pending';
  }

  for (const raw of candidates) {
    const direct = pickAllowedWithSynonyms(allowed, normalizeStatusKey(raw));
    if (direct) return direct;
  }

  for (const raw of candidates) {
    const dbLike = legacyLabelToDbStatus(raw);
    const uiFromDb = mapArchiveDbStatusToUiStatus(typeKey, dbLike);
    const resolved = pickAllowedWithSynonyms(allowed, uiFromDb);
    if (resolved) return resolved;

    const uiDirect = mapArchiveDbStatusToUiStatus(typeKey, normalizeStatusKey(raw));
    const resolvedDirect = pickAllowedWithSynonyms(allowed, uiDirect);
    if (resolvedDirect) return resolvedDirect;
  }

  return allowed.includes('pending') ? 'pending' : allowed[0] || 'pending';
}
